import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideBanknote,
  lucideChevronRight,
  lucideLogOut,
  lucideMenu,
  lucidePackage,
  lucideSettings,
  lucideTickets,
  lucideWallet,
} from '@ng-icons/lucide';
import { HlmAvatarImports } from '@spartan-ng/helm/avatar';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmSheetImports } from '@spartan-ng/helm/sheet';
import { HlmSidebarImports } from '@spartan-ng/helm/sidebar';
import { filter, map } from 'rxjs';
import { AuthService } from '../../../../services/auth.service';
import { Company } from '../../../../services/company.service';
import { SettingsNavStateService } from '../../../settings/settings-nav-state.service';
import { SettingsSidebarComponent } from '../../../settings/settings-sidebar/settings-sidebar.component';
import { companyInitials } from '../company-initials';
import { MAIN_NAV_LINKS } from '../nav-links';
import { SetupProgressComponent } from '../setup-progress/setup-progress.component';

const SETTINGS_ROUTE = '/settings';

/**
 * Hamburger trigger + navigation sheet: the one place to navigate on mobile.
 *
 * The main pages come first; "Einstellungen" unfolds into the settings
 * navigation (the same sidebar as on desktop, embedded), so the settings page
 * needs no menu of its own on mobile.
 */
@Component({
  selector: 'app-mobile-nav',
  imports: [
    RouterLink,
    RouterLinkActive,
    NgIcon,
    HlmAvatarImports,
    HlmButtonImports,
    HlmItemImports,
    HlmSeparatorImports,
    HlmSheetImports,
    HlmSidebarImports,
    SettingsSidebarComponent,
    SetupProgressComponent,
  ],
  providers: [
    provideIcons({
      lucideBanknote,
      lucideChevronRight,
      lucideLogOut,
      lucideMenu,
      lucidePackage,
      lucideSettings,
      lucideTickets,
      lucideWallet,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <hlm-sheet side="left" [state]="state()" (stateChanged)="onStateChange($event)">
      <button hlmSheetTrigger hlmBtn variant="ghost" size="icon" aria-label="Menü öffnen">
        <ng-icon name="lucideMenu" />
      </button>

      <hlm-sheet-content
        *hlmSheetPortal
        class="bg-sidebar text-sidebar-foreground w-[88%] max-w-sm gap-0 p-0"
        aria-label="Navigation">
        <div hlmSheetHeader>
          <h2 hlmSheetTitle>{{ company().name }}</h2>
          <p hlmSheetDescription>ID: {{ company().public_id }}</p>
        </div>

        <!-- Scrolls on its own, so the account row below stays in reach even with
             the settings unfolded. -->
        <nav class="min-h-0 flex-1 overflow-y-auto px-2 pb-4" aria-label="Hauptnavigation">
          <ul hlmSidebarMenu>
            @for (link of links(); track link.route) {
              <li hlmSidebarMenuItem>
                <a
                  hlmSidebarMenuButton
                  class="hover:text-sidebar-foreground active:text-sidebar-foreground data-active:hover:text-sidebar-accent-foreground data-active:active:text-sidebar-accent-foreground"
                  [routerLink]="link.route"
                  routerLinkActive
                  #rla="routerLinkActive"
                  [isActive]="rla.isActive"
                  (click)="close()">
                  <ng-icon [name]="link.icon" />
                  <span class="truncate">{{ link.label }}</span>
                </a>
              </li>
            }

            <!-- Unfolds in place instead of navigating: the settings have no page of
                 their own, only their sub pages. -->
            <li hlmSidebarMenuItem>
              <button
                hlmSidebarMenuButton
                class="hover:text-sidebar-foreground active:text-sidebar-foreground data-active:hover:text-sidebar-accent-foreground data-active:active:text-sidebar-accent-foreground"
                [isActive]="onSettings() && !settingsOpen()"
                [attr.aria-expanded]="settingsOpen()"
                aria-controls="mobile-nav-settings"
                (click)="settingsOpen.set(!settingsOpen())">
                <ng-icon name="lucideSettings" />
                <span class="truncate">Einstellungen</span>
                <ng-icon
                  name="lucideChevronRight"
                  class="text-muted-foreground ms-auto transition-transform duration-200"
                  [class.rotate-90]="settingsOpen()" />
              </button>
            </li>
          </ul>

          @if (settingsOpen()) {
            <div id="mobile-nav-settings" class="border-sidebar-border ms-3.5 border-s ps-2">
              <app-settings-sidebar
                [embedded]="true"
                [activeParam]="settingsParam()"
                [isMember]="!!company().is_member"
                [isNetwork]="!!company().is_group_admin"
                (select)="openSettings($event)"
                (picked)="close()" />
            </div>
          }

          <app-setup-progress bordered class="mx-2 mt-4 block" (navigated)="close()" />
        </nav>

        <div hlmSeparator></div>

        <div hlmItem class="rounded-none border-none">
          <div hlmItemMedia variant="image">
            <hlm-avatar>
              @if (logoUrl(); as url) {
                <img hlmAvatarImage [src]="url" [alt]="company().name" />
              }
              <span hlmAvatarFallback>{{ initials() }}</span>
            </hlm-avatar>
          </div>
          <div hlmItemContent>
            <span hlmItemTitle>{{ company().name }}</span>
          </div>
          <div hlmItemActions>
            <button hlmBtn variant="outline" size="sm" (click)="logout()">
              Logout
              <ng-icon name="lucideLogOut" data-icon="inline-end" />
            </button>
          </div>
        </div>
      </hlm-sheet-content>
    </hlm-sheet>
  `,
})
export class MobileNavComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  readonly company = input.required<Company>();
  readonly logoUrl = input<string | null>(null);

  /** Mirrors the sheet state so the layout can lock body scrolling. */
  readonly openChange = output<boolean>();

  protected readonly state = signal<'open' | 'closed'>('closed');
  /** Whether the settings entries are unfolded. */
  protected readonly settingsOpen = signal(false);

  /** The settings tab on screen, null outside the settings page. */
  protected readonly settingsParam = inject(SettingsNavStateService).activeParam;

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected readonly onSettings = computed(() => this.url().startsWith(SETTINGS_ROUTE));

  protected readonly initials = computed(() => companyInitials(this.company().name));

  /** The main pages; the settings get their own collapsible entry. */
  protected readonly links = computed(() =>
    MAIN_NAV_LINKS.filter(
      (link) =>
        link.route !== SETTINGS_ROUTE && (!link.isVisible || link.isVisible(this.company())),
    ),
  );

  protected onStateChange(state: 'open' | 'closed'): void {
    // On the settings page the menu opens with its entries already unfolded.
    if (state === 'open') this.settingsOpen.set(this.onSettings());
    this.state.set(state);
    this.openChange.emit(state === 'open');
  }

  protected openSettings(param: string): void {
    this.router.navigate([SETTINGS_ROUTE], { queryParams: { tab: param } });
  }

  protected close(): void {
    this.onStateChange('closed');
  }

  protected logout(): void {
    this.close();
    this.authService.logout();
  }
}
